var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * <vitesse-nav-tree> — the shared left-rail entity tree. DATA-DRIVEN and app-agnostic:
 * the app feeds it a ShellTreeNode[] (orgs→gateways→… for fleet, gateways→controllers→
 * symbols for the gateway app) and listens for `node-select`. It knows nothing about
 * managers, routes, or fetching — that inversion is what makes it reusable (the old
 * app-ui <vitesse-tree> was hard-coupled to ControllerManager/ModelManager).
 *
 * Styling is the tree-navigator.scss contract, ported into shadow CSS so the component
 * is self-contained (tokens pierce the shadow boundary). Expand/collapse uses the same
 * CSS grid-rows 1fr↔0fr animation as the reference.
 */
import { LitElement, html, svg, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import './vitesse-icon';
const chevron = svg `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"></polyline></svg>`;
let VitesseNavTree = class VitesseNavTree extends LitElement {
    constructor() {
        super(...arguments);
        /** The hierarchy to render. The app builds this from its own data. */
        this.nodes = [];
        /** The currently-selected node key (drives `.active`). */
        this.selectedKey = '';
        /** Keys the user has collapsed (default-expanded model). */
        this.collapsed = new Set();
        // Seed the collapsed set from any nodes flagged collapsed, once, when nodes arrive.
        this.seeded = false;
    }
    willUpdate(changed) {
        if (changed.has('nodes') && !this.seeded && this.nodes.length) {
            const seed = (ns) => {
                for (const n of ns) {
                    if (n.collapsed)
                        this.collapsed.add(n.key);
                    if (n.children)
                        seed(n.children);
                }
            };
            seed(this.nodes);
            this.seeded = true;
        }
    }
    static { this.styles = css `
    :host {
      display: block;
      width: 100%;
      height: 100%;
      background: var(--tree-bg, var(--bg-secondary, var(--bg-primary, #000)));
      overflow: hidden;
    }
    .tree-content {
      padding: 8px;
      height: 100%;
      overflow-y: auto;
      overflow-x: hidden;
      box-sizing: border-box;
    }
    .tree-node-wrapper {
      margin-bottom: 2px;
    }
    .tree-separator {
      height: 1px;
      background: var(--border-color, #2d2d2d);
      margin: 4px 0;
      opacity: 0.5;
    }
    .tree-node {
      display: flex;
      align-items: center;
      gap: 2px;
      position: relative;
    }
    .tree-label {
      flex: 1;
      display: flex;
      align-items: center;
      padding: 6px 8px;
      background: transparent;
      border: none;
      cursor: pointer;
      text-align: left;
      border-radius: 6px;
      transition: background-color 0.15s ease;
      font-size: 14px;
      color: var(--text-secondary, #9ca3af);
      min-height: 32px;
      min-width: 0;
      overflow: hidden;
      font-family: inherit;
    }
    .tree-label:hover,
    .tree-label.active {
      background: var(--hover-bg, rgba(255, 255, 255, 0.06));
      color: var(--text-primary, #fff);
    }
    .tree-icon {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-right: 6px;
      opacity: 0.6;
    }
    .tree-status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      flex-shrink: 0;
      margin-right: 7px;
      background: var(--text-secondary, #9ca3af);
    }
    .tree-status-dot.dot-green {
      background: #00ba7c;
    }
    .tree-status-dot.dot-yellow {
      background: #e2b13c;
      animation: tree-dot-pulse 1.4s ease-in-out infinite;
    }
    .tree-status-dot.dot-red {
      background: #e03e42;
    }
    .tree-status-dot.dot-gray {
      background: var(--text-secondary, #9ca3af);
      opacity: 0.55;
    }
    .tree-text {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
    }
    .tree-count {
      font-size: 11px;
      color: var(--text-secondary, #9ca3af);
      margin-left: auto;
      padding-left: 8px;
      flex-shrink: 0;
    }
    .tree-chevron {
      flex-shrink: 0;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      color: var(--text-secondary, #9ca3af);
      cursor: pointer;
      padding: 0;
      border-radius: 4px;
      transition: color 0.2s ease, transform 0.2s ease;
    }
    .tree-chevron svg {
      transition: transform 0.15s ease;
    }
    .tree-chevron:hover {
      color: var(--blue, #1a8cd8);
    }
    .tree-chevron:hover svg {
      transform: scale(1.2);
    }
    .tree-chevron.expanded {
      transform: rotate(90deg);
    }
    .tree-chevron-spacer {
      flex-shrink: 0;
      width: 20px;
    }
    .tree-children-wrapper {
      display: grid;
      transition: grid-template-rows 0.2s ease, opacity 0.2s ease;
    }
    .tree-children-overflow {
      overflow: hidden;
    }
    @keyframes tree-dot-pulse {
      0%,
      100% {
        opacity: 1;
      }
      50% {
        opacity: 0.35;
      }
    }
  `; }
    /** True when ANY node has children → the tree is hierarchical and needs the
     *  chevron column (childless rows get a spacer to align under chevroned ones).
     *  A fully FLAT tree (e.g. a list of views) has none → no chevron column, no
     *  indent. */
    get hasAnyChildren() {
        return this.nodes.some((n) => !!n.children?.length);
    }
    toggle(key) {
        const next = new Set(this.collapsed);
        next.has(key) ? next.delete(key) : next.add(key);
        this.collapsed = next;
    }
    select(key) {
        this.dispatchEvent(new CustomEvent('node-select', { detail: { key }, bubbles: true, composed: true }));
    }
    renderNode(node, depth) {
        const hasChildren = !!node.children?.length;
        const expanded = !this.collapsed.has(node.key);
        return html `
      ${node.separatorBefore ? html `<div class="tree-separator"></div>` : nothing}
      <div class="tree-node-wrapper">
        <div class="tree-node" style="padding-left:${depth * 14}px">
          ${this.hasAnyChildren
            ? hasChildren
                ? html `<button
                  class="tree-chevron ${expanded ? 'expanded' : 'collapsed'}"
                  aria-label=${expanded ? 'Collapse' : 'Expand'}
                  @click=${() => this.toggle(node.key)}
                >
                  ${chevron}
                </button>`
                : html `<span class="tree-chevron-spacer"></span>`
            : nothing}
          <button
            class="tree-label ${this.selectedKey === node.key ? 'active' : ''}"
            @click=${() => this.select(node.key)}
          >
            ${node.status
            ? html `<span class="tree-status-dot dot-${node.status}"></span>`
            : node.icon
                ? html `<span class="tree-icon"><vitesse-icon name=${node.icon}></vitesse-icon></span>`
                : nothing}
            <span class="tree-text">${node.label}</span>
            ${node.count != null ? html `<span class="tree-count">${node.count}</span>` : nothing}
          </button>
        </div>
        ${hasChildren
            ? html `<div class="tree-children-wrapper" style="grid-template-rows:${expanded ? '1fr' : '0fr'}">
              <div class="tree-children-overflow">
                ${node.children.map((c) => this.renderNode(c, depth + 1))}
              </div>
            </div>`
            : nothing}
      </div>
    `;
    }
    render() {
        return html `<div class="tree-content">${this.nodes.map((n) => this.renderNode(n, 0))}</div>`;
    }
};
__decorate([
    property({ attribute: false })
], VitesseNavTree.prototype, "nodes", void 0);
__decorate([
    property()
], VitesseNavTree.prototype, "selectedKey", void 0);
__decorate([
    state()
], VitesseNavTree.prototype, "collapsed", void 0);
VitesseNavTree = __decorate([
    customElement('vitesse-nav-tree')
], VitesseNavTree);
export { VitesseNavTree };
//# sourceMappingURL=vitesse-nav-tree.js.map